import { useState } from "preact/hooks";

import { ChangePasswordSheet } from "../components/ChangePasswordSheet";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { usePasskeys } from "../hooks/usePasskeys";
import {
  BIOMETRIC_STEP_ACCEPT,
  SETTINGS_ADD_PASSKEY_ROW,
  SETTINGS_PASSKEY_EMPTY_STATE,
  SETTINGS_PASSKEY_SECTION_HEADING,
  SETTINGS_PASSWORD_ACTION,
  SETTINGS_PASSWORD_CHANGED_SUCCESS,
  SETTINGS_REMOVE_PASSKEY_CONFIRM_BODY,
  SETTINGS_REMOVE_PASSKEY_CONFIRM_CANCEL,
  SETTINGS_REMOVE_PASSKEY_CONFIRM_CONFIRM,
  SETTINGS_REMOVE_PASSKEY_CONFIRM_TITLE,
} from "../lib/authCopy";
import { monthDayLabel } from "../lib/recordsCopy";
import type { PasskeyOut } from "../types";
import { PasskeyEnrollView } from "./PasskeyEnrollView";

export interface SignInSettingsViewProps {
  onNotify: (message: string) => void;
}

/**
 * Sprint-007 D6 (F4-5, UX §4.2) — the "Your sign-in" settings subview,
 * reached from `MoreView`'s new nav row. Visible to every authenticated
 * role now (UX §4: "admin, manager, and member alike now all have a
 * password... not role-gated the way Projects is").
 *
 * "Change your password" opens `ChangePasswordSheet` (a sheet, per UX
 * §4.2). "+ Add a quick sign-in" (or, with zero enrolled anywhere, the
 * "Turn it on" empty-state button) reuses `PasskeyEnrollView` VERBATIM —
 * "do not write a second version of this screen" — the same component
 * `app.tsx` shows post-onboarding.
 */
export function SignInSettingsView({ onNotify }: SignInSettingsViewProps) {
  const passkeys = usePasskeys();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<PasskeyOut | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  if (showEnroll) {
    return (
      <PasskeyEnrollView
        onDone={() => {
          setShowEnroll(false);
          void passkeys.refresh();
        }}
      />
    );
  }

  async function handleConfirmRemove() {
    if (!removeTarget) return;
    const target = removeTarget;
    setRemoveTarget(null);
    setRemoveError(null);
    try {
      await passkeys.remove(target.id);
    } catch {
      setRemoveError("Couldn't remove that — try again.");
    }
  }

  const hasAny = (passkeys.passkeys?.length ?? 0) > 0;

  return (
    <div>
      <ul class="admin-list">
        <li>
          <button type="button" class="admin-row" onClick={() => setShowChangePassword(true)}>
            <span class="admin-row-main">
              <span class="admin-row-name">{SETTINGS_PASSWORD_ACTION}</span>
            </span>
            <span class="chevron" aria-hidden="true">
              ›
            </span>
          </button>
        </li>
      </ul>

      <h2 class="view-subheading" style={{ marginTop: "24px" }}>
        {SETTINGS_PASSKEY_SECTION_HEADING}
      </h2>

      {passkeys.loading ? (
        <p class="loading-hint">Loading…</p>
      ) : passkeys.error ? (
        <p class="error-hint" role="alert">
          {passkeys.error}
        </p>
      ) : !hasAny ? (
        <>
          <p class="empty-hint">{SETTINGS_PASSKEY_EMPTY_STATE}</p>
          <button type="button" class="primary-btn" onClick={() => setShowEnroll(true)}>
            {BIOMETRIC_STEP_ACCEPT}
          </button>
        </>
      ) : (
        <>
          <ul class="admin-list">
            {passkeys.passkeys?.map((pk) => (
              <li key={pk.id}>
                <div class="admin-row">
                  <span class="admin-row-main">
                    <span class="admin-row-name">
                      {(pk.nickname ?? "This device") +
                        " — added " +
                        monthDayLabel(pk.created_at.slice(0, 10))}
                    </span>
                  </span>
                  <button type="button" class="secondary-btn" onClick={() => setRemoveTarget(pk)}>
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <ul class="admin-list">
            <li>
              <button type="button" class="admin-row" onClick={() => setShowEnroll(true)}>
                <span class="admin-row-main">
                  <span class="admin-row-name">{SETTINGS_ADD_PASSKEY_ROW}</span>
                </span>
              </button>
            </li>
          </ul>
        </>
      )}

      {removeError ? (
        <p class="error-hint" role="alert">
          {removeError}
        </p>
      ) : null}

      <ChangePasswordSheet
        open={showChangePassword}
        onDone={() => {
          setShowChangePassword(false);
          onNotify(SETTINGS_PASSWORD_CHANGED_SUCCESS);
        }}
        onCancel={() => setShowChangePassword(false)}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        title={SETTINGS_REMOVE_PASSKEY_CONFIRM_TITLE}
        body={SETTINGS_REMOVE_PASSKEY_CONFIRM_BODY}
        confirmLabel={SETTINGS_REMOVE_PASSKEY_CONFIRM_CONFIRM}
        cancelLabel={SETTINGS_REMOVE_PASSKEY_CONFIRM_CANCEL}
        destructive
        onConfirm={() => void handleConfirmRemove()}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}
